import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useScanner, SCAN_PROFILE } from '@/root/App/com/ScannerProvider/ScannerProvider';
import { SCAN_MODE } from '@/lib/scanning/scanMode';
import { operatorFacingMessage } from '@/lib/utils/errorUtils';
import { buzz, playFailTone, playSuccessTone } from '@/lib/scanning/feedback';
import { ZONE_BY_SCAN_CODE } from './zoneByScanCode';
import type { ScannedZone } from './zoneByScanCode';

const ZONE_TYPE_LABEL = { stock: 'stock', quarantine: 'quarantine' };

export const zoneTypeLabel = (zoneType?: string) =>
  ZONE_TYPE_LABEL[zoneType as keyof typeof ZONE_TYPE_LABEL] || zoneType || 'unknown';

/** "stock", or "stock or quarantine" for a multi-type step. */
const expectedTypeLabel = (zoneTypes: string[]) =>
  zoneTypes.map(zoneTypeLabel).join(' or ');

export type ZoneScanStatus =
  | { kind: 'scanning' }
  | { kind: 'resolving'; code: string }
  | { kind: 'notFound'; code: string }
  | { kind: 'rejected'; code: string; message: string }
  | { kind: 'ambiguous'; code: string; matches: ScannedZone[] }
  | { kind: 'matched'; zone: ScannedZone; code: string; matchedOn?: string | null };

export interface ZoneScanRules {
  companyId: string;
  /** Narrows resolution to one warehouse location when the caller knows it. */
  locationId?: string;
  /** Zone types this step accepts. A scan of any other type is refused with an explanation. */
  zoneTypes?: string[];
  /** Zones already spoken for by this screen (e.g. the source zone on a move). */
  excludeZoneIds?: string[];
  excludeMessage?: string;
  /**
   * When set, only these zones may be chosen — the picking screens pass the zones that
   * actually hold the line's product. Resolution still runs against every zone in the company
   * so a scan of an unrelated rack says "that zone has no stock of this product" rather than
   * the useless "zone not found".
   */
  allowedZones?: ScannedZone[] | null;
  allowedZonesMessage?: string;
}

/**
 * One zone scanner's worth of behaviour, shared by the full-screen overlay and the scanner built
 * into a form: it claims the shared camera / trigger for as long as it is mounted, resolves each
 * label, applies the screen's rules, and hands an accepted zone to `onAccepted` once the brief
 * confirmation has been on screen.
 *
 * Mounting is what claims the scanner and unmounting is what hands it back, so a caller shows a
 * zone scanner by rendering it and stops one by not rendering it.
 */
export function useZoneScanSession({
  companyId,
  locationId,
  zoneTypes,
  excludeZoneIds = [],
  excludeMessage,
  allowedZones = null,
  allowedZonesMessage,
  onAccepted
}: ZoneScanRules & { onAccepted: (zone: ScannedZone) => void }) {
  const targetRef = useRef(null);
  const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const {
    scanResult,
    clearScanResult,
    discardScanResult,
    pushScanTarget,
    popScanTarget,
    activeScanTarget,
    hardwareScanner,
    scanMode,
    cameraBlocked,
    cameraGranted
  } = useScanner();
  // Guards against handling the same scan twice: the effect below re-runs whenever a callback
  // identity changes (callers pass array literals), and a double submit would fire two lookups.
  // Seeded with whatever scan is already sitting in the provider when the scanner opens — the
  // item barcode that brought the operator to this screen is never this screen's zone.
  const handledScanRef = useRef(scanResult);
  // Set the moment a zone is accepted. The camera stays live during the short confirmation,
  // so without this a label still in frame could swap the zone out from under the close.
  const settledRef = useRef(false);
  const laserMode = scanMode === SCAN_MODE.LASER;
  const [status, setStatus] = useState<ZoneScanStatus>({ kind: 'scanning' });

  const [lookupZone] = useLazyQuery(ZONE_BY_SCAN_CODE, { fetchPolicy: 'network-only' });

  // The scanner renders above (or inside) whatever page opened it, so it must take the single
  // shared camera pipeline for as long as it is up and give it straight back when it goes.
  useEffect(() => {
    pushScanTarget(targetRef, SCAN_PROFILE.ZONE);
    return () => {
      popScanTarget(targetRef);
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
      // Drop any unconsumed scan so the page underneath does not pick a zone label up as an item.
      discardScanResult();
    };
  }, [pushScanTarget, popScanTarget, discardScanResult]);

  const ownsCamera = activeScanTarget === targetRef;

  const allowedIds = useMemo(
    () => (allowedZones ? new Set(allowedZones.map(({ id }) => id)) : null),
    [allowedZones]
  );
  // Callers pass fresh array literals every render, so memoize on the contents rather than
  // the identity — otherwise every dependent callback below churns on each render.
  const excludeKey = excludeZoneIds.join(',');
  const excludedIds = useMemo(
    () => new Set(excludeZoneIds.filter(Boolean)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [excludeKey]
  );
  const zoneTypesKey = zoneTypes?.join(',');

  /** Why this zone cannot be used here, or null when it is a valid choice. */
  const rejectionFor = useCallback(
    (zone: ScannedZone): string | null => {
      if (excludedIds.has(zone.id)) {
        return excludeMessage || `${zone.name} is already selected on this screen.`;
      }
      if (zoneTypes?.length && !zoneTypes.includes(zone.zoneType || '')) {
        return `${zone.name} is a ${zoneTypeLabel(
          zone.zoneType
        )} zone — this step needs a ${expectedTypeLabel(zoneTypes)} zone.`;
      }
      if (allowedIds && !allowedIds.has(zone.id)) {
        return allowedZonesMessage || `${zone.name} is not available for this step.`;
      }
      return null;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [excludedIds, excludeMessage, zoneTypesKey, allowedIds, allowedZonesMessage]
  );

  const acceptZone = useCallback(
    (zone: ScannedZone, scanned?: { code: string; matchedOn?: string | null }) => {
      if (settledRef.current) return;
      const rejection = rejectionFor(zone);
      if (rejection) {
        buzz([60, 80, 60]);
        playFailTone();
        setStatus({ kind: 'rejected', code: zone.name, message: rejection });
        return;
      }
      buzz(40);
      playSuccessTone();
      settledRef.current = true;
      setStatus({
        kind: 'matched',
        zone,
        code: scanned?.code || '',
        matchedOn: scanned?.matchedOn
      });
      // Hold the confirmation on screen just long enough to read the zone name before handing
      // control back — a silent instant close leaves the operator unsure what was captured.
      successTimeoutRef.current = setTimeout(() => {
        successTimeoutRef.current = null;
        onAccepted(zone);
      }, 550);
    },
    [rejectionFor, onAccepted]
  );

  const resolveCode = useCallback(
    async (code: string) => {
      setStatus({ kind: 'resolving', code });
      try {
        const { data, error } = await lookupZone({
          variables: { companyId, code, locationId }
        });
        if (error) throw error;
        const result = data?.zoneByScanCode;
        const matches: ScannedZone[] = result?.matches || [];
        if (result?.zone) {
          acceptZone(result.zone, { code, matchedOn: result.matchedOn });
          return;
        }
        if (matches.length > 1) {
          buzz([60, 80, 60]);
          playFailTone();
          setStatus({ kind: 'ambiguous', code, matches });
          return;
        }
        buzz([60, 80, 60]);
        playFailTone();
        setStatus({ kind: 'notFound', code });
      } catch (e) {
        buzz([60, 80, 60]);
        playFailTone();
        setStatus({
          kind: 'rejected',
          code,
          message: operatorFacingMessage(
            e,
            'Could not look up that zone. Please try again.'
          )
        });
      }
    },
    [acceptZone, companyId, locationId, lookupZone]
  );

  useEffect(() => {
    if (!scanResult || !ownsCamera || settledRef.current) return;
    if (handledScanRef.current === scanResult) return;
    handledScanRef.current = scanResult;
    // A zone label is not a GS1 item barcode, so the parsed fields are empty and the raw
    // payload is the only thing worth reading.
    const code = (scanResult.text || '').trim();
    // Re-arm quickly: the operator is standing at the rack and may need a second try. The 3s
    // item-scan default reads as a frozen app here.
    clearScanResult(900);
    if (!code) {
      buzz([60, 80, 60]);
      playFailTone();
      setStatus({ kind: 'notFound', code: '' });
      return;
    }
    resolveCode(code);
  }, [scanResult, ownsCamera, clearScanResult, resolveCode]);

  const scanAgain = useCallback(() => setStatus({ kind: 'scanning' }), []);

  return {
    targetRef,
    status,
    laserMode,
    /**
     * What the camera view says while it shows no picture. `listOffered`: the scanner has a
     * button to the zone list, so the way out can point at it — otherwise it must not.
     */
    cameraMessage: (listOffered: boolean) => {
      if (cameraBlocked) {
        return listOffered
          ? 'The camera is blocked. Choose the zone from the list below, or switch to Laser.'
          : 'The camera is blocked. Switch to Laser, or allow the camera in the browser settings.';
      }
      return cameraGranted
        ? 'Starting the camera…'
        : 'Allow RxERP to use your device’s camera.';
    },
    hardwareDetected: !!hardwareScanner?.detected,
    rejectionFor,
    acceptZone,
    scanAgain
  };
}

export default useZoneScanSession;
